<script setup>
import { computed, ref, watch, inject, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import ConversationCard from './widgets/conversation/ConversationCard.vue';
import ConversationCardExpanded from 'dashboard/components-next/Conversation/ConversationCard/ConversationCardExpanded.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import ConversationContextMenu from './widgets/conversation/contextMenu/Index.vue';
import { getContextMenuCoordinates } from './widgets/conversation/contextMenu/helpers';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationSwipe } from 'dashboard/composables/chatlist/useConversationSwipe';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  source: { type: Object, required: true },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showAssignee: { type: Boolean, default: false },
  showExpanded: { type: Boolean, default: false },
  positionChangeDirection: { type: String, default: '' },
  isNewConversation: { type: Boolean, default: false },
});

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const selectConversation = inject('selectConversation');
const deSelectConversation = inject('deSelectConversation');
const assignAgent = inject('assignAgent');
const assignTeam = inject('assignTeam');
const assignLabels = inject('assignLabels');
const removeLabels = inject('removeLabels');
const updateConversationStatus = inject('updateConversationStatus');
const markAsUnread = inject('markAsUnread');
const markAsRead = inject('markAsRead');
const assignPriority = inject('assignPriority');
const isConversationSelected = inject('isConversationSelected');
const deleteConversation = inject('deleteConversation');
const clearConversation = inject('clearConversation');
const isConversationPinned = inject('isConversationPinned');
const togglePinnedConversation = inject('togglePinnedConversation');

// --- Context menu state (shared by both layouts) ---
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset context menu state when the row is recycled to a different conversation.
watch(
  () => props.source.id,
  () => {
    showContextMenu.value = false;
    contextMenu.value = { x: null, y: null };
  }
);

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.source.meta || {});
const assignee = computed(() => chatMetadata.value.assignee || {});
const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() =>
  senderId.value ? store.getters['contacts/getContact'](senderId.value) : {}
);

const isActiveChat = computed(() => currentChat.value.id === props.source.id);
const isPinned = computed(() => isConversationPinned(props.source.id));

const inbox = computed(() => {
  const inboxId = props.source.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});

const showInboxName = computed(
  () => !activeInbox.value && inboxesList.value.length > 1
);
const isInboxView = computed(() => !!activeInbox.value);
const showAssigneeForExpandedCard = computed(
  () => props.showExpanded || props.showAssignee
);

const conversationPath = computed(() =>
  frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.source.id,
      label: props.label,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  )
);

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  if (isActiveChat.value) return;
  router.push({ path });
};

const onExpandedSelect = checked => {
  if (checked) {
    selectConversation(props.source.id, inbox.value.id);
  } else {
    deSelectConversation(props.source.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  e.preventDefault();
  contextMenu.value = getContextMenuCoordinates(e);
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  updateConversationStatus(props.source.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  assignAgent(agent, [props.source.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  assignLabels([label.title], [props.source.id]);
};

const onRemoveLabel = label => {
  removeLabels([label.title], [props.source.id]);
};

const onAssignTeam = team => {
  assignTeam(team, props.source.id);
  closeContextMenu();
};

const onMarkAsUnread = () => {
  markAsUnread(props.source.id);
  closeContextMenu();
};

const onMarkAsRead = () => {
  markAsRead(props.source.id);
  closeContextMenu();
};

const onAssignPriority = priority => {
  assignPriority(priority, props.source.id);
  closeContextMenu();
};

const onClearConversation = () => {
  clearConversation(props.source.id);
  closeContextMenu();
};

const onDeleteConversation = () => {
  deleteConversation(props.source.id);
  closeContextMenu();
};

const onTogglePin = () => {
  const { limitReached } = togglePinnedConversation(props.source.id);
  closeContextMenu();

  if (limitReached) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.PIN_LIMIT_REACHED'));
  }
};

// --- Deslizar para o lado (toque): ações rápidas da conversa ---
const { isAdmin } = useAdmin();
const SWIPE_ACTION_WIDTH = 72;
const swipeActions = computed(() => [
  {
    key: 'pin',
    label: isPinned.value
      ? t('CONVERSATION.SWIPE_ACTIONS.UNPIN')
      : t('CONVERSATION.SWIPE_ACTIONS.PIN'),
    icon: isPinned.value ? 'i-hugeicons-pin-off' : 'i-hugeicons-pin',
    className: 'bg-n-brand text-white',
  },
  {
    key: 'more',
    label: t('CONVERSATION.SWIPE_ACTIONS.MORE'),
    icon: 'i-hugeicons-more-horizontal',
    className: 'bg-n-slate-11 text-n-solid-1',
  },
  ...(isAdmin.value
    ? [
        {
          key: 'delete',
          label: t('CONVERSATION.SWIPE_ACTIONS.DELETE'),
          icon: 'i-hugeicons-delete-02',
          className: 'bg-n-ruby-9 text-white',
        },
      ]
    : []),
]);
const swipeActionsWidth = computed(
  () => swipeActions.value.length * SWIPE_ACTION_WIDTH
);
const swipe = useConversationSwipe({
  id: computed(() => props.source.id),
  actionsWidth: swipeActionsWidth,
  enabled: computed(() => !props.showExpanded),
});
const swipeOffset = swipe.offset;
const isSwipeDragging = swipe.dragging;
// Mantém ações e fundo durante a animação de recolher (200 ms).
const isSwipeSettling = ref(false);
let swipeSettleTimer = null;
watch(swipeOffset, (value, previous) => {
  clearTimeout(swipeSettleTimer);
  if (value !== 0 || previous === 0) return;
  isSwipeSettling.value = true;
  swipeSettleTimer = setTimeout(() => {
    isSwipeSettling.value = false;
  }, 220);
});
onBeforeUnmount(() => clearTimeout(swipeSettleTimer));
const isSwipeVisible = computed(
  () => swipeOffset.value !== 0 || isSwipeSettling.value
);

const onSwipeAction = (key, event) => {
  if (key === 'pin') {
    swipe.close();
    onTogglePin();
    return;
  }
  if (key === 'more') {
    const { left, bottom } = event.currentTarget.getBoundingClientRect();
    swipe.close();
    openContextMenu(
      new MouseEvent('contextmenu', {
        clientX: left,
        clientY: bottom,
        cancelable: true,
      })
    );
    return;
  }
  swipe.close();
  deleteConversation(props.source.id);
};
</script>

<template>
  <div
    class="conversation-list-item"
    :class="{
      'conversation-list-item--new': isNewConversation,
      'conversation-list-item--position-changed': positionChangeDirection,
      'conversation-list-item--position-up': positionChangeDirection === 'up',
      'conversation-list-item--position-down':
        positionChangeDirection === 'down',
    }"
  >
    <!-- Expanded layout: wide screen + expanded setting -->
    <ConversationCardExpanded
      v-if="showExpanded"
      :chat="source"
      :current-contact="currentContact"
      :assignee="assignee"
      :inbox="inbox"
      :selected="isConversationSelected(source.id)"
      :is-active-chat="isActiveChat"
      :is-pinned="isPinned"
      :show-assignee="showAssigneeForExpandedCard"
      :show-inbox-name="showInboxName"
      :is-inbox-view="isInboxView"
      @select-conversation="onExpandedSelect"
      @de-select-conversation="onExpandedSelect"
      @click="onCardClick"
      @contextmenu="openContextMenu"
    />

    <!-- Default (condensed) layout: deslizar para a esquerda revela ações -->
    <div
      v-else
      class="conversation-swipe relative"
      @touchstart.passive="swipe.onTouchStart"
      @touchmove.passive="swipe.onTouchMove"
      @touchend="swipe.onTouchEnd"
      @touchcancel="swipe.onTouchEnd"
      @click.capture="swipe.onClickCapture"
    >
      <div
        v-show="isSwipeVisible"
        data-swipe-actions
        class="absolute inset-y-1 flex overflow-hidden rounded-2xl ltr:right-2 rtl:left-2"
        :style="{ width: `${swipeActionsWidth}px` }"
        role="group"
        :aria-label="t('CONVERSATION.SWIPE_ACTIONS.LABEL')"
      >
        <button
          v-for="action in swipeActions"
          :key="action.key"
          type="button"
          class="reset-base m-0 flex h-full flex-1 flex-col items-center justify-center gap-1 rounded-none px-1 text-xs font-medium"
          :class="action.className"
          @click.stop="onSwipeAction(action.key, $event)"
        >
          <span :class="action.icon" class="size-5" aria-hidden="true" />
          <span class="max-w-full truncate">{{ action.label }}</span>
        </button>
      </div>
      <div
        class="relative"
        :class="{
          'bg-n-surface-1': isSwipeVisible,
          'transition-transform duration-200 ease-out motion-reduce:transition-none':
            !isSwipeDragging,
        }"
        :style="
          isSwipeVisible
            ? { transform: `translate3d(${swipeOffset}px, 0, 0)` }
            : undefined
        "
      >
        <ConversationCard
          :actions-menu-open="showContextMenu"
          :chat="source"
          :current-contact="currentContact"
          :assignee="assignee"
          :inbox="inbox"
          :selected="isConversationSelected(source.id)"
          :is-active-chat="isActiveChat"
          :is-pinned="isPinned"
          :show-assignee="showAssignee"
          :show-inbox-name="showInboxName"
          @click="onCardClick"
          @contextmenu="openContextMenu"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
        />
      </div>
    </div>
  </div>

  <!-- Shared context menu for both layouts -->
  <ContextMenu
    v-if="showContextMenu"
    :x="contextMenu.x"
    :y="contextMenu.y"
    @close="closeContextMenu"
  >
    <ConversationContextMenu
      :status="source.status"
      :inbox-id="inbox.id"
      :priority="source.priority"
      :chat-id="source.id"
      :has-unread-messages="source.unread_count > 0"
      :conversation-labels="source.labels"
      :conversation-url="conversationPath"
      :is-pinned="isPinned"
      @update-conversation="onUpdateConversation"
      @assign-agent="onAssignAgent"
      @assign-label="onAssignLabel"
      @remove-label="onRemoveLabel"
      @assign-team="onAssignTeam"
      @mark-as-unread="onMarkAsUnread"
      @mark-as-read="onMarkAsRead"
      @assign-priority="onAssignPriority"
      @clear-conversation="onClearConversation"
      @delete-conversation="onDeleteConversation"
      @toggle-pin="onTogglePin"
      @close="closeContextMenu"
    />
  </ContextMenu>
</template>

<style scoped>
.conversation-swipe {
  touch-action: pan-y;
}

.conversation-list-item {
  position: relative;
  transform-origin: center;
  will-change: auto;
}

.conversation-list-item--new,
.conversation-list-item--position-changed {
  will-change: transform, opacity;
  z-index: 1;
}

.conversation-list-item--new {
  animation: conversation-new-arrival 520ms cubic-bezier(0.22, 1, 0.36, 1);
}

.conversation-list-item--position-up {
  animation: conversation-rise-to-top 640ms cubic-bezier(0.22, 1, 0.36, 1);
}

.conversation-list-item--position-down {
  animation: conversation-make-room 480ms cubic-bezier(0.22, 1, 0.36, 1);
}

@keyframes conversation-new-arrival {
  0% {
    opacity: 0;
    transform: translate3d(0, -8px, 0);
  }

  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes conversation-rise-to-top {
  0% {
    opacity: 0.88;
    transform: translate3d(0, 20px, 0);
  }

  100% {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
}

@keyframes conversation-make-room {
  0% {
    transform: translate3d(0, -10px, 0);
  }

  100% {
    transform: translate3d(0, 0, 0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .conversation-list-item--new,
  .conversation-list-item--position-changed {
    animation: none;
  }
}
</style>
